import { faGrid } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOktaAuth } from '@okta/okta-react';
import { DialogTrigger } from '@repo/videa-components/src/ui/dialog';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';

import {
  isNoteGenerationActiveAtom,
  isRecordingSessionActiveAtom,
} from '@/modules/videaNotes/atoms';
import { VideaNotesButton } from '@/modules/videaNotes/components/VideaNotesButton';

import { parsePathname } from '../../app/appHeader/path-utils';
import {
  isDirectorUser as getIsDirectorUser,
  isMultiPracticeUser,
} from '../../modules/login/login-utils';
import { usePreferences } from '../../react-query/usePreferences';
import routesConstants from '../../routes/routes.constants';
import { VideaAILogo } from '../../svgs/VideaAILogo';
import { useIsEmbedded } from '../../utils/hooks';
import { directorPracticeIdAtom } from '../../utils/hooks/useDirectorPracticeView';

import { AccountDropdown } from './AccountDropdown';
import { CustomerSupport } from './CustomerSupport';
import { PatientInfo } from './ImageViewerHeader/PatientInfo';
import { PatientListButton } from './ImageViewerHeader/PatientListButton';
import { VisitSelector } from './ImageViewerHeader/VisitSelector';
import { LearningCenter } from './LearningCenter';
import { Navigation } from './Navigation';
import { PracticeGroupsButton } from './PracticeGroupsButton';
import { PracticesDialog } from './PracticesDialog';

export const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const isEmbedded = useIsEmbedded();
  const { data: preferences } = usePreferences();
  const user = useSelector((state: any) => state?.login?.user);
  const isDirectorUser = getIsDirectorUser(user);
  const [directorPracticeId] = useAtom(directorPracticeIdAtom);
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isNoteGenerationActive = useAtomValue(isNoteGenerationActiveAtom);
  const { authState } = useOktaAuth();
  const query = new URLSearchParams(location.search);

  // Check if we're in the annotator (image viewer) context
  const isInAnnotator = history.location.pathname.includes(
    routesConstants.ANNOTATOR,
  );

  // Check if sidebar redesign is enabled
  const isSidebarRedesign = preferences?.useSidebarRedesign;

  // Check if we're in the external visit sidebar or modal
  const isInExternalVisitSidebar = history.location.pathname.includes(
    routesConstants.EXTERNAL_VISIT_SIDEBAR,
  );
  const isInExternalModal = history.location.pathname.includes(
    routesConstants.EXTERNAL_MODAL,
  );

  if (
    !user.username ||
    !authState?.isAuthenticated ||
    history.location.pathname.includes(routesConstants.LANDING_PAGE) ||
    (isEmbedded && !isSidebarRedesign) ||
    (isInAnnotator && !isSidebarRedesign) ||
    isInExternalVisitSidebar ||
    isInExternalModal
  ) {
    return null;
  }

  // Get patient ID for image viewer context
  const { patientId } = isInAnnotator
    ? parsePathname(location.pathname, isEmbedded)
    : { patientId: '' };

  const handleBackToPatientList = () => {
    if (query.get('source') === 'insights' && query.get('providerId')) {
      const providerId = query.get('providerId');
      history.push(`/insights/clinical/provider/${providerId}`);
    } else if (query.get('source') === 'insights') {
      history.push(`/insights/clinical`);
    } else {
      history.push('/uploader');
    }
  };

  return (
    <div
      className="vai-bg-navy-500 vai-h-16 vai-w-full vai-box-border vai-flex vai-items-center vai-p-8 vai-gap-4 vai-overflow-hidden"
      data-testid="header-component"
    >
      {!isEmbedded ? (
        <Link
          to="/"
          className={classNames('vai-flex vai-items-center vai-mr-4')}
        >
          <VideaAILogo />
        </Link>
      ) : (
        <img
          src="/assets/images/ddai/dentrix-videaai.svg"
          alt="Dentrix Videa Logo"
          className="vai-h-10 vai-mr-4"
        />
      )}

      {isInAnnotator && isSidebarRedesign && (
        // Image viewer components (only when sidebar redesign is enabled)
        <>
          {!isEmbedded && (
            <PatientListButton
              isInsights={query.get('source') === 'insights'}
              onClick={handleBackToPatientList}
            />
          )}
          <div className="vai-flex vai-flex-col vai-gap-1">
            <PatientInfo patientId={patientId} />
          </div>
          <div className="vai-flex vai-items-center vai-gap-2">
            <VisitSelector patientId={patientId} />
          </div>
        </>
      )}

      {!isInAnnotator && !isEmbedded && (
        // Regular navigation (only when NOT in annotator/embedded)
        <Navigation />
      )}

      <div className="vai-ml-auto vai-flex vai-items-center vai-gap-2">
        {!isMultiPracticeUser(user) ? (
          <span
            className="vai-text-lg vai-font-inter vai-font-medium vai-text-white"
            data-testid="practice-name"
          >
            {user?.primaryPracticeName}
          </span>
        ) : directorPracticeId ? (
          <PracticesDialog>
            <DialogTrigger className="vai-flex vai-items-center vai-gap-2 vai-text-white vai-bg-[#123059] vai-rounded-md vai-py-2 vai-px-3">
              <FontAwesomeIcon icon={faGrid} />
              <span
                className="vai-text-lg vai-font-inter vai-font-medium vai-tracking-tighter"
                data-testid="practice-menu-trigger"
              >
                {user?.primaryPracticeName}
              </span>
            </DialogTrigger>
          </PracticesDialog>
        ) : (
          <span
            className="vai-text-lg vai-font-inter vai-font-medium vai-text-white"
            data-testid="org-name"
          >
            {user?.primaryOrganizationName}
          </span>
        )}
        {isDirectorUser && preferences?.enableCustomPracticeGroups && (
          <PracticeGroupsButton />
        )}
        {preferences?.isVideaNotesEnabled &&
          ((isInAnnotator && isSidebarRedesign) ||
            isRecordingSessionActive ||
            isNoteGenerationActive) && <VideaNotesButton />}

        {/* hide LearningCenter and CustomerSupport buttons when there is an active recording session */}
        {!isRecordingSessionActive && !isNoteGenerationActive && (
          <>
            <LearningCenter />
            <CustomerSupport />
          </>
        )}
        <div className="vai-ml-2">
          <AccountDropdown />
        </div>
      </div>
    </div>
  );
};
